import "react-circular-progressbar/dist/styles.css";
import { ChevronUp, ChevronDown } from "lucide-react";
import { useState, useEffect, useRef } from "react";
import Layout from "../../../components/layout/layout";

import QuickExpert from "../../../assets/dashboard/quickExpert.svg";

import Interaction from "../../../assets/dashboard/interaction.svg";
import AmountSpend from "../../../assets/dashboard/amountSpend.svg";
import tooltipSmall from "../../../assets/dashboard/tooltipSmall.png";
import videoIconSmall from "../../../assets/dashboard/videoIconSmall.png";
import TooltipBigicon from "../../../assets/dashboard/tooltipbig.svg";
import DateRangePicker from "../../../components/DateRange/DateRangePicker";
import DownlodExtention from "../../../common/Dashboard/DownloadExtentionCard";
import SetUpTeamCard from "../../../common/Dashboard/SetUpTeamCard";
import QuickSupportCard from "../../../common/Dashboard/QuickSupportCard";
import InteractionsCard from "../../../components/Dashboard/InteractionsCard";
import ChartCard, {
  CircularChart,
  Rating,
  StatsCounter,
} from "../../../components/Dashboard/ChartCard";
import { useSelector, useDispatch } from "react-redux";
import { RootState, AppDispatch } from "../../../store";
import { useAppState } from "../../../hooks/useAppState";
import { fetchActivitiesByRole } from "../../../store/slices/activitySlice";
import {
  fetchAllTeamData,
  filterMembersTeams,
} from "../../../store/slices/teamSlice";
import { fetchTooltipSales } from "../../../store/slices/tooltipSlice";

const ExpertDashboard = () => {
  const dispatch = useDispatch<AppDispatch>();
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [dateRange, setDateRange] = useState<{
    start: string | null;
    end: string | null;
  }>({
    start: null,
    end: null,
  });
  const [timeframeDropdownOpen, setTimeframeDropdownOpen] = useState(false);
  const [selectedTimeframe, setSelectedTimeframe] = useState("");
  const [statsDropdownOpen, setStatsDropdownOpen] = useState(false);
  const [selectedStatsOption, setSelectedStatsOption] = useState("Me");
  const { user } = useAppState();
  // Refs to detect outside clicks for timeframe dropdown and date picker
  const timeframeRef = useRef<HTMLDivElement | null>(null);
  const datePickerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      const target = event.target as Node;

      if (
        timeframeRef.current &&
        !timeframeRef.current.contains(target)
      ) {
        setTimeframeDropdownOpen(false);
      }

      if (datePickerRef.current && !datePickerRef.current.contains(target)) {
        setShowDatePicker(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [timeframeRef, datePickerRef]);
  const { filteredMembers, members, currentTeam } = useSelector(
    (state: any) => state.team
  );
  const profileData = useSelector((state: RootState) => state.profile.user);

  // Use expertActivities from activitySlice
  const expertActivities = useSelector(
    (state: RootState) => state.activity.expertActivities || []
  );

  // Use tooltip sales data from tooltipSlice
  const { tooltipSales } = useSelector(
    (state: RootState) => state.tooltip
  );

  // Helper function to filter activities by timeframe
  const getFilteredActivitiesByTimeframe = (
    activities: any[],
    timeframe: string
  ) => {
    if (!timeframe || !activities) return activities;

    const now = new Date();
    const filterDate = new Date();

    switch (timeframe) {
      case "this-month":
        filterDate.setMonth(now.getMonth(), 1);
        filterDate.setHours(0, 0, 0, 0);
        break;
      case "last-3-months":
        filterDate.setMonth(now.getMonth() - 3);
        filterDate.setHours(0, 0, 0, 0);
        break;
      case "last-12-months":
        filterDate.setFullYear(now.getFullYear() - 1);
        filterDate.setHours(0, 0, 0, 0);
        break;
      case "ytd":
        filterDate.setFullYear(now.getFullYear(), 0, 1);
        filterDate.setHours(0, 0, 0, 0);
        break;
      default:
        return activities;
    }

    return activities.filter((activity: any) => {
      // Handle Firestore timestamp objects and regular Date objects
      let activityDate = null;

      if (activity.createdAt) {
        activityDate = activity.createdAt.toDate
          ? activity.createdAt.toDate()
          : new Date(activity.createdAt);
      } else if (activity.startTime) {
        activityDate = activity.startTime.toDate
          ? activity.startTime.toDate()
          : new Date(activity.startTime);
      } else if (activity.timestamp) {
        activityDate = activity.timestamp.toDate
          ? activity.timestamp.toDate()
          : new Date(activity.timestamp);
      } else if (activity.date) {
        activityDate = new Date(activity.date);
      }

      return activityDate && activityDate >= filterDate;
    });
  };

  // Helper function to filter activities by custom date range
  const getFilteredActivitiesByDateRange = (
    activities: any[],
    dateRange: any
  ) => {
    if (!dateRange.start || !dateRange.end || !activities) return activities;

    const startDate = new Date(dateRange.start);
    const endDate = new Date(dateRange.end);
    endDate.setHours(23, 59, 59, 999); // Include the entire end date

    return activities.filter((activity: any) => {
      // Handle Firestore timestamp objects and regular Date objects
      let activityDate = null;

      if (activity.createdAt) {
        activityDate = activity.createdAt.toDate
          ? activity.createdAt.toDate()
          : new Date(activity.createdAt);
      } else if (activity.startTime) {
        activityDate = activity.startTime.toDate
          ? activity.startTime.toDate()
          : new Date(activity.startTime);
      } else if (activity.timestamp) {
        activityDate = activity.timestamp.toDate
          ? activity.timestamp.toDate()
          : new Date(activity.timestamp);
      } else if (activity.date) {
        activityDate = new Date(activity.date);
      }

      return (
        activityDate && activityDate >= startDate && activityDate <= endDate
      );
    });
  };

  // Fetch team data when component mounts
  useEffect(() => {
    const fetchTeamData = async () => {
      if (!user?.orgId || !user?.uid) {
        return;
      }

      try {
        // Dispatch the fetchAllTeamData action from the team slice
        await dispatch(
          fetchAllTeamData({
            orgId: user.orgId,
            userId: user.teamId || user.uid,
            currentId: user?.uid,
            userRole: user?.teamRole,
          })
        ).unwrap();
      } catch (error) {
        console.error("Error fetching team data:", error);
      }
    };

    fetchTeamData();
  }, [dispatch, user?.orgId, user?.uid, user?.uiRole]);
  // Fetch activities from activitySlice when component mounts or user changes
  useEffect(() => {
    if (user && user?.uid) {
      dispatch(
        fetchActivitiesByRole({ role: "Expert", userId: user?.uid }) as any
      );
    }
  }, [dispatch, user]);

  // Update filtered members when team data changes
  useEffect(() => {
    if (members?.length > 0 && user) {
      dispatch(
        filterMembersTeams({
          searchTerm: "",
          userRole: user?.teamRole ,
          userId: user?.teamId || user?.uid,
        })
      );
    }
  }, [dispatch, members, user]);

  // Fetch tooltip sales data when component mounts or user changes
  useEffect(() => {
    if (user && user?.uid) {
      dispatch(fetchTooltipSales(user.uid));
    }
  }, [dispatch, user?.uid]);

  function formatRole(role) {
    switch (role) {
      case "expert_owner":
        return "Team Owner";
      case "expert_team_lead":
        return "Team Lead";
      case "expert_team_member":
        return "Team Member";
      default:
        return "Expert";
    }
  }
  // Filter and format activities for Quick Experts with timeframe and date range filtering
  const baseFilteredActivities = Array.isArray(expertActivities)
    ? expertActivities.filter(
        (activity: any) =>
          activity.type === "Video Call" ||
          activity.type === "Chat" ||
          activity.status === "completed" ||
          activity.status === "Issue reported" ||
          activity.status === "active"
      )
    : [];

  // Apply timeframe filter
  const timeFrameFilteredActivities = selectedTimeframe
    ? getFilteredActivitiesByTimeframe(
        baseFilteredActivities,
        selectedTimeframe
      )
    : baseFilteredActivities;

  // Apply date range filter if custom date range is set
  const filteredActivities =
    dateRange.start && dateRange.end
      ? getFilteredActivitiesByDateRange(timeFrameFilteredActivities, dateRange)
      : timeFrameFilteredActivities;

  // Filter out invited users for stats dropdown and active team member count
  const activeFilteredMembers =
    filteredMembers && filteredMembers.length > 0
      ? filteredMembers.filter((member: any) => member.status !== "invited")
      : [];
  console.log(activeFilteredMembers, "activeFilteredMembers");
  const statsOptions = [
    { value: "Me", label: "Me" },
    { value: "All", label: "All" },
    ...activeFilteredMembers.map((member: any) => ({
      value: member.id || member.uid,
      label: `${member.name} (${formatRole(member.role)})`,
    })),
  ];
  // Calculate total amount spent from activities
  const totalSessionsAmount = filteredActivities.reduce(
    (total: number, activity: any) => {
      // Try multiple ways to extract the amount
      let amount = 0;

      if (activity.cost) {
        amount =
          typeof activity.cost === "string"
            ? parseFloat(activity.cost.replace("$", ""))
            : parseFloat(activity.cost);
      } else if (activity.amount) {
        amount =
          typeof activity.amount === "string"
            ? parseFloat(activity.amount.replace("$", ""))
            : parseFloat(activity.amount);
      } else if (activity.metadata?.amount) {
        amount =
          typeof activity.metadata.amount === "string"
            ? parseFloat(activity.metadata.amount.replace("$", ""))
            : parseFloat(activity.metadata.amount);
      }

      return total + (isNaN(amount) ? 0 : amount);
    },
    0
  );

  const highRatedSessions = filteredActivities.filter((activity: any) => {
    const rating = parseFloat(activity.customerRating);
    return rating >= 4;
  });
  const satisfactionScore =
    filteredActivities.length > 0
      ? Math.round((highRatedSessions.length / filteredActivities.length) * 100)
      : 0; // Calculate dynamic ratings data based on actual sessionActivities
  const calculateRatingDistribution = (activities: any[]): Rating[] => {
    const ratingsCount = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
    let totalRatedActivities = 0;

    activities.forEach((activity: any) => {
      if (activity.customerRating) {
        const rating = Math.round(parseFloat(activity.customerRating));
        if (rating >= 1 && rating <= 5) {
          ratingsCount[rating as keyof typeof ratingsCount]++;
          totalRatedActivities++;
        }
      }
    });

    // Calculate percentages (will be 0 if no ratings exist)
    const percentage5 =
      totalRatedActivities > 0
        ? Math.round((ratingsCount[5] / totalRatedActivities) * 100)
        : 0;
    const percentage4 =
      totalRatedActivities > 0
        ? Math.round((ratingsCount[4] / totalRatedActivities) * 100)
        : 0;
    const percentage3 =
      totalRatedActivities > 0
        ? Math.round((ratingsCount[3] / totalRatedActivities) * 100)
        : 0;
    const percentage2 =
      totalRatedActivities > 0
        ? Math.round((ratingsCount[2] / totalRatedActivities) * 100)
        : 0;
    const percentage1 =
      totalRatedActivities > 0
        ? Math.round((ratingsCount[1] / totalRatedActivities) * 100)
        : 0;

    return [
      { stars: 5, percentage: percentage5, count: ratingsCount[5] },
      { stars: 4, percentage: percentage4, count: ratingsCount[4] },
      { stars: 3, percentage: percentage3, count: ratingsCount[3] },
      { stars: 2, percentage: percentage2, count: ratingsCount[2] },
      { stars: 1, percentage: percentage1, count: ratingsCount[1] },
    ];
  };

  const quickExpertRatings: Rating[] =
    calculateRatingDistribution(filteredActivities);

  // Filter tooltip sales by timeframe and date range
  const baseFilteredTooltipSales = Array.isArray(tooltipSales) ? tooltipSales : [];

  // Apply timeframe filter to tooltip sales
  const timeFrameFilteredTooltipSales = selectedTimeframe
    ? getFilteredActivitiesByTimeframe(baseFilteredTooltipSales, selectedTimeframe)
    : baseFilteredTooltipSales;

  // Apply date range filter if custom date range is set
  const filteredTooltipSales =
    dateRange.start && dateRange.end
      ? getFilteredActivitiesByDateRange(timeFrameFilteredTooltipSales, dateRange)
      : timeFrameFilteredTooltipSales;

  // Calculate tooltip sales satisfaction score
  const highRatedTooltipSales = filteredTooltipSales.filter((sale) => {
    const rating = parseFloat(sale.rating || "0");
    return rating >= 4;
  });
  
  const tooltipSatisfactionScore =
    filteredTooltipSales.length > 0
      ? Math.round((highRatedTooltipSales.length / filteredTooltipSales.length) * 100)
      : 0;

  // Calculate tooltip sales ratings distribution
  const calculateTooltipRatingDistribution = (sales: typeof filteredTooltipSales): Rating[] => {
    const ratingsCount = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
    let totalRatedSales = 0;

    sales.forEach((sale) => {
      if (sale.rating) {
        const rating = Math.round(parseFloat(sale.rating as string));
        if (rating >= 1 && rating <= 5) {
          ratingsCount[rating as keyof typeof ratingsCount]++;
          totalRatedSales++;
        }
      }
    });

    // Calculate percentages (will be 0 if no ratings exist)
    const percentage5 =
      totalRatedSales > 0
        ? Math.round((ratingsCount[5] / totalRatedSales) * 100)
        : 0;
    const percentage4 =
      totalRatedSales > 0
        ? Math.round((ratingsCount[4] / totalRatedSales) * 100)
        : 0;
    const percentage3 =
      totalRatedSales > 0
        ? Math.round((ratingsCount[3] / totalRatedSales) * 100)
        : 0;
    const percentage2 =
      totalRatedSales > 0
        ? Math.round((ratingsCount[2] / totalRatedSales) * 100)
        : 0;
    const percentage1 =
      totalRatedSales > 0
        ? Math.round((ratingsCount[1] / totalRatedSales) * 100)
        : 0;

    return [
      { stars: 5, percentage: percentage5, count: ratingsCount[5] },
      { stars: 4, percentage: percentage4, count: ratingsCount[4] },
      { stars: 3, percentage: percentage3, count: ratingsCount[3] },
      { stars: 2, percentage: percentage2, count: ratingsCount[2] },
      { stars: 1, percentage: percentage1, count: ratingsCount[1] },
    ];
  };

  const tooltipSalesRatings: Rating[] = calculateTooltipRatingDistribution(filteredTooltipSales);

  // Calculate total tooltip sales amount from filtered data
  const filteredTooltipSalesAmount = filteredTooltipSales.reduce((total, sale) => {
    return total + (sale.price || 0);
  }, 0);
  const timeframeOptions = [
    { value: "", label: "All time" },
    { value: "this-month", label: "This month" },
    { value: "last-3-months", label: "Last 3 months" },
    { value: "last-12-months", label: "Last 12 months" },
    { value: "ytd", label: "Year to date" },
  ];
  const handleTimeframeSelect = (value: string) => {
    setSelectedTimeframe(value);
    setTimeframeDropdownOpen(false);
    // Clear custom date range when timeframe is selected
    if (value) {
      setDateRange({ start: null, end: null });
    }
  };

  const handleStatsSelect = (_value: string, label: string) => {
    setSelectedStatsOption(label);
    setStatsDropdownOpen(false);
  };
  const handleDateRangeApply = (
    startDate: Date | null,
    endDate: Date | null
  ) => {
    setDateRange({
      start: startDate ? startDate.toISOString() : null,
      end: endDate ? endDate.toISOString() : null,
    });
    // Clear timeframe when custom date range is selected
    if (startDate && endDate) {
      setSelectedTimeframe("");
    }
    setShowDatePicker(false);
  };

  const handleDateRangeCancel = () => {
    setShowDatePicker(false);
  };

  return (
    <Layout>
      <div
        className="min-h-screen bg-white rounded-xl"
        style={{
          boxShadow: " 0px 2px 2px 0px #00000040",
        }}
      >
        {/* Header */}

        {/* Main Content */}
        <div className="p-6">
          {" "}
          <div className="mb-6 flex justify-between">
            <div>
              <h1 className="text-[20px] nunito-semibold text-[#696969]">
                Dashboard
              </h1>
              <p className="text-[#919191] nunito-regular mt-1">
                Hi {profileData?.name}, Welcome to Supporty.io! Let’s get you
                started!
              </p>
              <p className="text-[#919191] nunito-regular">
                And whenever you need, you can always come back here to track
                all your key activities in one place.
              </p>
            </div>{" "}
            {user?.teamRole !== "expert_team_member" && (
              <div className="">
                <label className="text-[#696969] mr-1  text-[20px] nunito-semibold">
                  Stats for:
                </label>
                <div className="relative inline-block group">
                  <button
                    onClick={() => setStatsDropdownOpen(!statsDropdownOpen)}
                    disabled
                    className="h-[40px] w-[280px] border-[#A8A8A8] border-[1px] size-[16px] text-[#696969] nunito-semibold rounded-[8px] mx-3 px-3 flex items-center justify-between"
                  >
                    {selectedStatsOption || "Me"}
                    {statsDropdownOpen ? (
                      <ChevronUp className="w-4 h-4" />
                    ) : (
                      <ChevronDown className="w-4 h-4" />
                    )}
                  </button>
                   {/* Tooltip on hover */}
                  <div className="absolute left-1/2 -translate-x-1/2 -top-8 px-2 py-1 bg-[#FAFAFA] nunito-regular text-[12px] text-[#696969] text-xs rounded-[8px] shadow-lg opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity duration-200 whitespace-nowrap z-30">
                    This feature is on its way - stay tuned!
                  </div>
                  {statsDropdownOpen && (
                    <div className="absolute left-3 right-0 mt-1 w-[280px] bg-white border border-[#A8A8A8] rounded-[8px] z-20">
                      {statsOptions.map((option) => (
                        <div
                          key={option.value}
                          className="px-3 py-2 hover:bg-gray-100 cursor-pointer text-[#696969] nunito-semibold"
                          onClick={() =>
                            handleStatsSelect(option.value, option.label)
                          }
                        >
                          {option.label}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
          {/*  Download Extension Now */}
          <DownlodExtention />
          {/* Hero Section using QuickSupportCard component */}
          <QuickSupportCard isExpertView={true} />
          {/* Setup Team */}
          {user?.teamRole !== "expert_team_member" && currentTeam == null && (
            <SetUpTeamCard user={user} />
          )}
          {/* Timeframe Selection */}
          <div className="flex justify-end gap-4 mt-4 mb-4 relative">
            <div className="relative" ref={timeframeRef}>
              <button
                onClick={() => setTimeframeDropdownOpen(!timeframeDropdownOpen)}
                className="text-[#404040] Roboto-regular w-[150px] text-[16px]  py-1 flex items-center gap-2  rounded"
              >
                {selectedTimeframe
                  ? timeframeOptions.find(
                      (opt) => opt.value === selectedTimeframe
                    )?.label
                  : "Select timeframe"}
                {timeframeDropdownOpen ? (
                  <ChevronUp className="w-4 h-4" />
                ) : (
                  <ChevronDown className="w-4 h-4" />
                )}
              </button>
              {timeframeDropdownOpen && (
                <div className="absolute left-0 top-full text-[#404040] sixe-[16px] nunito-regular z-10 mt-1 bg-white shadow-md rounded w-full">
                  {" "}
                  {timeframeOptions.map((option) => (
                    <div
                      key={option.value}
                      className="px-3 py-2 hover:bg-gray-100 cursor-pointer"
                      onClick={() => handleTimeframeSelect(option.value)}
                    >
                      {option.label}
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="relative" ref={datePickerRef}>
              {" "}
              <button
                onClick={() => setShowDatePicker(!showDatePicker)}
                className="flex items-center text-[#404040] Roboto-regular text-[16px] gap-2 px-3 py-1"
              >
                {dateRange.start && dateRange.end
                  ? `${new Date(dateRange.start).toLocaleDateString("en-US", {
                      month: "short",
                      day: "numeric",
                      year: "numeric",
                    })} - ${new Date(dateRange.end).toLocaleDateString(
                      "en-US",
                      {
                        month: "short",
                        day: "numeric",
                        year: "numeric",
                      }
                    )}`
                  : "Custom range "}
                {showDatePicker ? (
                  <ChevronUp className="w-4 h-4" />
                ) : (
                  <ChevronDown className="w-4 h-4" />
                )}
              </button>
              {showDatePicker && (
                <div className="absolute right-0 top-full z-10 mt-1">
                  <DateRangePicker
                    onApply={handleDateRangeApply}
                    onCancel={handleDateRangeCancel}
                  />
                </div>
              )}
            </div>{" "}
          </div>
          <hr className="text-[#EBEBEB] border-t mb-6 border-[3px]" />{" "}
          {/* Interaction cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
            {" "}
            {/* Interactions Card */}
            <InteractionsCard
              icon={Interaction}
              title="Interactions"
              value={(0 + 0 + filteredActivities.length).toString()}
            />
            {/* Amount Card */}
            <InteractionsCard
              icon={AmountSpend}
              title="Earnings"
              value={`$${(totalSessionsAmount + filteredTooltipSalesAmount).toFixed(2)}`}
              details={[
                {
                  icon: () => (
                    <img
                      src={videoIconSmall}
                      alt="videoIconSmall"
                      className="w-[24px] h-[24px] "
                    />
                  ),
                  label: "Expert Sessions",
                  value: `$${totalSessionsAmount.toFixed(2)}`,
                },
                {
                  icon: () => (
                    <img
                      src={tooltipSmall}
                      alt="tooltip"
                      className="w-[24px] h-[24px] "
                    />
                  ),
                  label: "Tooltip Sales",
                  value: `$${filteredTooltipSalesAmount.toFixed(2)}`,
                },
              ]}
            />
          </div>{" "}
          {/* Charts Section */}
          <div className="grid grid-cols-3 md:grid-cols-3 gap-6">
            {/* Quick Experts Chart */}
            <ChartCard icon={QuickExpert} title="Quick Experts" CirculTitle="">
              <StatsCounter
                items={[
                  { label: "#Sessions", value: filteredActivities.length },
                ]}
                className="mt-4 text-[#0A0A0A]"
              />
              <CircularChart
                value={satisfactionScore}
                circulTitle="Satisfaction Score"
                label="% of sessions with rating 4 or 5"
                ratings={quickExpertRatings}
              />
            </ChartCard>

            {/* Tooltip Sales Summary */}
            <div className="relative group">
              <ChartCard
                icon={TooltipBigicon}
                title="Tooltip Sales Summary"
                CirculTitle=""
              >
                <StatsCounter
                  items={[{ label: "#Sales", value: filteredTooltipSales.length }]}
                  className="mt-4 text-[#0A0A0A]"
                />
                <CircularChart
                  value={tooltipSatisfactionScore}
                  circulTitle="Satisfaction Score"
                  label="% of tooltip sales with rating 4 or 5"
                  ratings={tooltipSalesRatings}
                />
               
              </ChartCard>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default ExpertDashboard;
